import { ref } from 'vue'
import { ADMIN_URL } from '../config/api'

const API_BASE = `${ADMIN_URL}/whatsapp`

function getAuthHeaders() {
  const token = localStorage.getItem('auth_token')
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

async function apiCall(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: { ...getAuthHeaders(), ...options.headers },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || error.detail || 'Request failed')
  }

  const result = await response.json()
  return result.data !== undefined ? result.data : result
}

export function useWhatsAppAdmin() {
  const loading = ref(false)
  const error = ref(null)

  // ============================================================================
  // WABA Account Management
  // ============================================================================

  const listWABAAccounts = async () => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const getWABAAccount = async (id) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${id}`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const createWABAAccount = async (data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const updateWABAAccount = async (id, data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const deleteWABAAccount = async (id) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${id}`, {
        method: 'DELETE',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const testWABAConnection = async (id) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${id}/test`, {
        method: 'POST',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const syncWABA = async (id) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${id}/sync`, {
        method: 'POST',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // ============================================================================
  // Phone Number Management
  // ============================================================================

  const listPhoneNumbers = async (wabaId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${wabaId}/phones`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const createPhoneNumber = async (wabaId, data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${wabaId}/phones`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const updatePhoneNumber = async (wabaId, phoneId, data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${wabaId}/phones/${phoneId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const deletePhoneNumber = async (wabaId, phoneId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${wabaId}/phones/${phoneId}`, {
        method: 'DELETE',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const syncPhoneNumbers = async (wabaId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${wabaId}/phones/sync`, {
        method: 'POST',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // ============================================================================
  // Template Management
  // ============================================================================

  const listTemplates = async (wabaId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${wabaId}/templates`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const syncTemplates = async (wabaId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${wabaId}/templates/sync`, {
        method: 'POST',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const updateTemplateMapping = async (wabaId, templateId, data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/wabas/${wabaId}/templates/${templateId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // ============================================================================
  // Business WABA Access
  // ============================================================================

  const listBusinessOverviews = async () => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const listBusinessWABAs = async (businessId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/wabas`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const assignWABAToBusiness = async (businessId, data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/wabas`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const removeWABAFromBusiness = async (businessId, wabaId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/wabas/${wabaId}`, {
        method: 'DELETE',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // ============================================================================
  // Business Phone Assignments
  // ============================================================================

  const listBusinessPhones = async (businessId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/phones`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const assignPhoneToBusiness = async (businessId, data) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/phones`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const removePhoneFromBusiness = async (businessId, phoneId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/phones/${phoneId}`, {
        method: 'DELETE',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const setBusinessPrimaryPhone = async (businessId, phoneId) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/businesses/${businessId}/phones/${phoneId}/primary`, {
        method: 'PUT',
      })
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // ============================================================================
  // Analytics
  // ============================================================================

  const getAnalyticsSummary = async (params = {}) => {
    loading.value = true
    error.value = null
    try {
      const queryString = new URLSearchParams(params).toString()
      const url = queryString
        ? `${API_BASE}/analytics/summary?${queryString}`
        : `${API_BASE}/analytics/summary`
      return await apiCall(url)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const getAnalyticsByBusiness = async (params = {}) => {
    loading.value = true
    error.value = null
    try {
      const queryString = new URLSearchParams(params).toString()
      const url = queryString
        ? `${API_BASE}/analytics/by-business?${queryString}`
        : `${API_BASE}/analytics/by-business`
      return await apiCall(url)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const getAnalyticsByDay = async (params = {}) => {
    loading.value = true
    error.value = null
    try {
      const queryString = new URLSearchParams(params).toString()
      const url = queryString
        ? `${API_BASE}/analytics/by-day?${queryString}`
        : `${API_BASE}/analytics/by-day`
      return await apiCall(url)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // ============================================================================
  // Message Logs
  // ============================================================================

  const listMessageLogs = async (params = {}) => {
    loading.value = true
    error.value = null
    try {
      const queryString = new URLSearchParams(params).toString()
      const url = queryString
        ? `${API_BASE}/logs?${queryString}`
        : `${API_BASE}/logs`
      return await apiCall(url)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  const getMessageLog = async (id) => {
    loading.value = true
    error.value = null
    try {
      return await apiCall(`${API_BASE}/logs/${id}`)
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    // WABA Accounts
    listWABAAccounts,
    getWABAAccount,
    createWABAAccount,
    updateWABAAccount,
    deleteWABAAccount,
    testWABAConnection,
    syncWABA,
    // Phone Numbers
    listPhoneNumbers,
    createPhoneNumber,
    updatePhoneNumber,
    deletePhoneNumber,
    syncPhoneNumbers,
    // Templates
    listTemplates,
    syncTemplates,
    updateTemplateMapping,
    // Business WABA Access
    listBusinessOverviews,
    listBusinessWABAs,
    assignWABAToBusiness,
    removeWABAFromBusiness,
    // Business Phones
    listBusinessPhones,
    assignPhoneToBusiness,
    removePhoneFromBusiness,
    setBusinessPrimaryPhone,
    // Analytics
    getAnalyticsSummary,
    getAnalyticsByBusiness,
    getAnalyticsByDay,
    // Message Logs
    listMessageLogs,
    getMessageLog,
  }
}
